import { useSnackbars } from '@customerio/pluma-components/react';
import plur from 'plur';
import { useCallback } from 'react';

import { useDeleteAssets } from '@/services/db/asset/mutations';
import { sentryCaptureException } from '@/utils/sentry';
import { type Asset } from '@/services/db/asset/entity';

import { useRefetchAssets } from './use-refetch-assets';

export function useDeleteAssetsAction({ onDeleted }: { onDeleted: (deletedAssets: Asset[]) => void }) {
  const deleteAssetsMutation = useDeleteAssets();
  const snackbars = useSnackbars();
  const refetchAssets = useRefetchAssets();

  const confirmDelete = useCallback(
    async (assetsToDelete: Asset[]) => {
      if (assetsToDelete.length === 0) return;

      try {
        await deleteAssetsMutation.mutateAsync({
          asset_ids: assetsToDelete.map((a) => a.id),
        });

        const itemCount = assetsToDelete.length;
        snackbars.create({
          variant: 'success',
          message: `${itemCount} ${plur('item', itemCount)} deleted successfully`,
        });

        onDeleted(assetsToDelete);
        refetchAssets();
      } catch (error) {
        sentryCaptureException(error);
        snackbars.create({
          variant: 'error',
          message: 'Failed to delete assets',
        });
      }
    },
    [deleteAssetsMutation, snackbars, refetchAssets, onDeleted],
  );

  return confirmDelete;
}
